import { matchPath } from 'react-router-dom';
import { logExceptionOnce, logUnknownErrorResponse } from '@hungryroot/o11y';
import {
  getApiV2CustomersOccasionEntityPreferences,
  postApiV2CustomersCustomerIdResetPlan,
  StatusCode,
} from '@hungryroot/rest';
import { ROUTE_MINI_QUIZ_SWIPE } from '@hungryroot/routes';
import {
  MINI_QUIZ_ROUTE_SEQUENCES,
  type MiniQuizSequenceKey,
} from '../defs/mini-quiz-routes';
import { readMiniQuizResumeState } from '../navigation/miniQuizResume/storage';

/**
 * Whether a guest has been through the explicit-selections swipe deck
 * (`/mini-quiz/food-preferences-intro/` → `/mini-quiz/swipe/`).
 *
 *   - `complete`   — the deck was finished, locally in this browser or on
 *                    the account.
 *   - `incomplete` — neither says so.
 *   - `unknown`    — no local evidence and the account read failed. Callers
 *                    should fall back to whatever they did before this check
 *                    existed rather than send the guest into the deck.
 */
type ExplicitSelectionsCompletion = 'complete' | 'incomplete' | 'unknown';

/**
 * The `rating_context`s the swipe deck writes. `funnel` is the mini-quiz
 * deck (`server-persist`); `onboarding` is the post-checkout deck (Exp 238),
 * which also counts — the guest has rated explicit selections either way.
 * `food_profile` ratings are edits made elsewhere and are not a deck pass.
 */
const EXPLICIT_SELECTIONS_RATING_CONTEXTS = new Set(['funnel', 'onboarding']);

/**
 * One page is plenty: a pre-checkout guest's preferences are the deck's own
 * rows, so any funnel/onboarding row surfaces well inside it.
 */
const PREFERENCES_PAGE_SIZE = 500;

const SEQUENCE_KEYS = Object.keys(
  MINI_QUIZ_ROUTE_SEQUENCES
) as MiniQuizSequenceKey[];

function getRouteIndexInSequence(
  pathname: string,
  sequenceKey: MiniQuizSequenceKey
): number {
  return MINI_QUIZ_ROUTE_SEQUENCES[sequenceKey].findIndex((entry) =>
    matchPath(pathname, { path: entry.path, exact: true, strict: false })
  );
}

/**
 * Whether this browser's mini-quiz resume state shows the swipe step
 * committed — the visitor finished or skipped the deck here within the
 * 24h resume window. Checked against both sequences: the swipe step sits
 * before signup in v2 and after it in v1, but a committed step at or past it
 * means the same thing in either.
 *
 * This is what keeps the v1 terminal CTA from bouncing a guest who just
 * "skipped all" back into the deck: a skipped deck writes no ratings, so the
 * account alone would read as incomplete.
 */
export function hasLocallyCompletedExplicitSelections(): boolean {
  const committed = readMiniQuizResumeState()?.lastCommittedPathname;
  if (committed === undefined) {
    return false;
  }

  return SEQUENCE_KEYS.some((sequenceKey) => {
    const committedIndex = getRouteIndexInSequence(committed, sequenceKey);
    const swipeIndex = getRouteIndexInSequence(
      ROUTE_MINI_QUIZ_SWIPE,
      sequenceKey
    );
    return (
      committedIndex !== -1 && swipeIndex !== -1 && committedIndex >= swipeIndex
    );
  });
}

/**
 * Whether the signed-in account holds any explicit-selections rating.
 * `null` when the read failed. There is no server-side "deck completed"
 * flag; the deck's own `PATCH /customers/self/occasion_entity_preferences/`
 * rows are the only durable trace it leaves.
 */
export async function fetchHasServerExplicitSelections(): Promise<
  boolean | null
> {
  try {
    const resp = await getApiV2CustomersOccasionEntityPreferences({
      limit: PREFERENCES_PAGE_SIZE,
    });
    if (resp.status !== StatusCode.SuccessOK) {
      logUnknownErrorResponse('Failed to read explicit selections', {
        key: 'explicitSelectionsCompletion.preferences',
        status: resp.status,
      });
      return null;
    }

    return resp.data.results.some(
      (row) =>
        row.rating_context !== undefined &&
        EXPLICIT_SELECTIONS_RATING_CONTEXTS.has(row.rating_context)
    );
  } catch (error) {
    logExceptionOnce(error, {
      key: 'explicitSelectionsCompletion.preferences.threw',
    });
    return null;
  }
}

/**
 * Local evidence first (free, and the only signal a skipped deck leaves),
 * then the account. Only called for a signed-in, pre-checkout guest.
 */
export async function resolveExplicitSelectionsCompletion(): Promise<ExplicitSelectionsCompletion> {
  if (hasLocallyCompletedExplicitSelections()) {
    return 'complete';
  }

  const hasServerSelections = await fetchHasServerExplicitSelections();
  if (hasServerSelections === null) {
    return 'unknown';
  }

  return hasServerSelections ? 'complete' : 'incomplete';
}

/**
 * Run before sending a guest to `/mini-quiz/food-preferences-intro/`.
 *
 * Every `/mini-quiz/*` mount (`MiniQuizIsland`) calls `reset_survey` for a
 * returning guest, which — when the plan is old enough — wipes the quiz
 * *and* every occasion-entity preference, then bounces to question 1. That
 * is the opposite of "go finish explicit selections", and the Exp 260
 * audience (existing guests) is exactly who has an old plan. `reset_plan`
 * (BE-8695) is the sanctioned way around it: it deactivates only the plan
 * and writes the `RESET_SURVEY` log that makes the later `reset_survey` a
 * no-op, so the answers and the deck survive and the plan page still builds
 * a fresh plan. Same call the login persist makes.
 *
 * Best effort: a failure is logged and the redirect proceeds — the guest
 * then gets today's reset behaviour rather than being stranded.
 */
export async function prepareExplicitSelectionsEntry({
  customerId,
}: {
  customerId: number;
}): Promise<void> {
  try {
    const resp = await postApiV2CustomersCustomerIdResetPlan({ customerId });
    if (resp.status !== StatusCode.SuccessOK) {
      logUnknownErrorResponse(
        'Plan reset before explicit selections was rejected',
        {
          key: 'explicitSelectionsCompletion.resetPlan',
          status: resp.status,
          customerId,
        }
      );
    }
  } catch (error) {
    logExceptionOnce(
      error,
      { key: 'explicitSelectionsCompletion.resetPlan.threw' },
      { customerId }
    );
  }
}
